import {
    Component,
    OnInit,
    ViewChild,
    ElementRef,
    AfterViewInit,
    HostListener
} from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '@app/store/app.state';
import { selectSettings } from '@app/store/selectors/settings.selector';
import { Settings } from '@app/models/settings.model';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-color-palette',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './color-palette.html',
    styleUrls: ['./color-palette.css']
})
export class ColorPalette implements OnInit, AfterViewInit {
    @ViewChild('spectrumCanvas', { static: true }) spectrumCanvas!: ElementRef<HTMLCanvasElement>;
    @ViewChild('lightnessCanvas', { static: true }) lightnessCanvas!: ElementRef<HTMLCanvasElement>;

    settings!: Settings;
    maxColors!: number;

    spectrumWidth = 256;
    spectrumHeight = 160;
    lightnessHeight = 20;

    lightness: number = 0.5;
    selectedColor: { r: number; g: number; b: number } = { r: 0, g: 0, b: 0 };

    // New: to track current H/S values for lightness variation
    currentHue: number = 0;
    currentSat: number = 0;

    constructor(private store: Store<AppState>) { }

    ngOnInit(): void {
        this.store.select(selectSettings).subscribe(settings => {
            this.settings = settings;
            this.maxColors = Math.pow(2, 24);
            this.drawSpectrum();      // now draws on lightnessCanvas
            this.drawLightnessBar();  // now draws on spectrumCanvas
        });
    }

    ngAfterViewInit(): void {
        this.drawSpectrum();
        this.drawLightnessBar();
    }

    drawSpectrum(): void {
        const canvas = this.lightnessCanvas.nativeElement;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        canvas.width = this.spectrumWidth;
        canvas.height = this.lightnessHeight;

        const image = ctx.createImageData(this.spectrumWidth, this.lightnessHeight);
        const data = image.data;

        for (let y = 0; y < this.lightnessHeight; y++) {
            const sat = 0.5 + (y / this.lightnessHeight) * 0.8;
            //const sat = y / this.lightnessHeight;
            //const sat = 1 - (y / this.lightnessHeight)
            for (let x = 0; x < this.spectrumWidth; x++) {
                let hue = x / this.spectrumWidth;
                const hueIndex = Math.floor(hue * this.maxColors);
                hue = hueIndex / this.maxColors;

                const rgb = this.hslToRgb(hue, sat, this.lightness);
                const i = (y * this.spectrumWidth + x) * 4;
                data[i] = rgb.r;
                data[i + 1] = rgb.g;
                data[i + 2] = rgb.b;
                data[i + 3] = 255;
            }
        }

        ctx.putImageData(image, 0, 0);
    }

    drawLightnessBar(): void {
        const canvas = this.spectrumCanvas.nativeElement;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        canvas.width = this.spectrumWidth;
        canvas.height = this.spectrumHeight;

        const image = ctx.createImageData(this.spectrumWidth, this.spectrumHeight);
        const data = image.data;

        for (let y = 0; y < this.spectrumHeight; y++) {
            const verticalFactor = 1 - (y / this.spectrumHeight); // Invert Y: bottom = 0, top = 1

            for (let x = 0; x < this.spectrumWidth; x++) {
                const horizontalFactor = x / this.spectrumWidth;  // Left = 0, Right = 1

                // Combine horizontal and vertical lightness in a diagonal blend
                const diagonalLightness = (horizontalFactor + verticalFactor) / 2;

                const rgb = this.hslToRgb(this.currentHue, this.currentSat, diagonalLightness);

                const i = (y * this.spectrumWidth + x) * 4;
                data[i] = rgb.r;
                data[i + 1] = rgb.g;
                data[i + 2] = rgb.b;
                data[i + 3] = 255;
            }
        }

        ctx.putImageData(image, 0, 0);
    }


    @HostListener('mousedown', ['$event'])
    onMouseDown(event: MouseEvent): void {
        const spectrumRect = this.spectrumCanvas.nativeElement.getBoundingClientRect();
        const lightnessRect = this.lightnessCanvas.nativeElement.getBoundingClientRect();

        if (this.inRect(event, lightnessRect)) {
            const x = Math.floor(event.clientX - lightnessRect.left);
            const y = Math.floor(event.clientY - lightnessRect.top);

            const hue = x / this.spectrumWidth;
            const sat = 0.5 + (y / this.lightnessHeight) * 0.8;
            //const sat = 1 - (y / this.lightnessHeight)
            //const sat = y / this.lightnessHeight;

            const hueIndex = Math.floor(hue * this.maxColors);
            const quantizedHue = hueIndex / this.maxColors;

            this.currentHue = quantizedHue;
            this.currentSat = sat;

            const rgb = this.hslToRgb(quantizedHue, sat, this.lightness);
            this.selectedColor = rgb;

            this.drawLightnessBar(); // update large canvas
        } else if (this.inRect(event, spectrumRect)) {
            const x = event.clientX - spectrumRect.left;
            const y = event.clientY - spectrumRect.top;

            const horizontalFactor = x / this.spectrumWidth;
            const verticalFactor = 1 - (y / this.spectrumHeight); // inverted Y

            this.lightness = (horizontalFactor + verticalFactor) / 2;

            const rgb = this.hslToRgb(this.currentHue, this.currentSat, this.lightness);
            this.selectedColor = rgb;

            this.drawLightnessBar(); // refresh with new lightness
        }
    }

    inRect(event: MouseEvent, rect: DOMRect): boolean {
        return (
            event.clientX >= rect.left &&
            event.clientX <= rect.right &&
            event.clientY >= rect.top &&
            event.clientY <= rect.bottom
        );
    }

    hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
        let r: number, g: number, b: number;

        if (s === 0) {
            r = g = b = l;
        } else {
            const hue2rgb = (p: number, q: number, t: number) => {
                if (t < 0) t += 1;
                if (t > 1) t -= 1;
                if (t < 1 / 6) return p + (q - p) * 6 * t;
                if (t < 1 / 2) return q;
                if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
                return p;
            };

            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1 / 3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1 / 3);
        }

        return {
            r: Math.round(r * 255),
            g: Math.round(g * 255),
            b: Math.round(b * 255)
        };
    }

    rgbToHex(c: { r: number; g: number; b: number }): string {
        return (
            "#" +
            ((1 << 24) | (c.r << 16) | (c.g << 8) | c.b)
                .toString(16)
                .slice(1)
                .toUpperCase()
        );
    }
}
